import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';
import './SettingsManagement.css';
import './WhatsAppSettings.css';
import RTPageLayout from '../common/RTPageLayout';

const WhatsAppSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/api/whatsapp/settings');

      // apiGet returns axios response, data is in response.data
      const data = response.data || response;

      if (data && Array.isArray(data)) {
        setSettings(data);

        // Initialize form data
        const initialFormData = {};
        data.forEach((setting) => {
          initialFormData[setting.setting_key] = setting.setting_value;
        });
        setFormData(initialFormData);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp settings:', error);
      toastError('Failed to load WhatsApp settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (settingKey, value) => {
    setFormData((prev) => ({
      ...prev,
      [settingKey]: value,
    }));
  };

  const handleSaveSetting = async (settingKey) => {
    try {
      setSaving(true);

      await apiPost(`/api/whatsapp/settings/${settingKey}`, {
        setting_value: formData[settingKey],
      });

      toastSuccess('Setting updated successfully');

      // Refresh settings to get updated data
      await fetchSettings();
    } catch (error) {
      console.error('Error saving setting:', error);
      toastError('Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  const renderSettingInput = (setting) => {
    if (setting.setting_type === 'boolean') {
      return (
        <div className="form-check form-switch">
          <input
            className="form-check-input whatsapp-settings-switch"
            type="checkbox"
            id={setting.setting_key}
            checked={formData[setting.setting_key] === 'true'}
            onChange={(e) =>
              handleInputChange(
                setting.setting_key,
                e.target.checked ? 'true' : 'false',
              )
            }
          />
          <label
            className="form-check-label whatsapp-settings-switch-label"
            htmlFor={setting.setting_key}
          >
            {formData[setting.setting_key] === 'true' ? 'Enabled' : 'Disabled'}
          </label>
        </div>
      );
    } else if (setting.setting_type === 'number') {
      // Number type
      return (
        <input
          type="number"
          className="form-control"
          value={formData[setting.setting_key] || ''}
          onChange={(e) =>
            handleInputChange(setting.setting_key, e.target.value)
          }
          placeholder={`Enter ${setting.setting_key}`}
          min="0"
          step="1"
        />
      );
    } else {
      // String type
      return (
        <input
          type="text"
          className="form-control"
          value={formData[setting.setting_key] || ''}
          onChange={(e) =>
            handleInputChange(setting.setting_key, e.target.value)
          }
          placeholder={`Enter ${setting.setting_key}`}
        />
      );
    }
  };

  if (loading) {
    return (
      <RTPageLayout
        activePath={location.pathname}
        title="WhatsApp Settings"
        actions={
          <RTButton
            variant="primary"
            onClick={() => navigate('/settings/whatsapp-templates')}
          >
            Template Management
          </RTButton>
        }
        mainSection={true}
      >
        <div className="whatsapp-settings-spinner-container">
          <RTSpinner />
        </div>
      </RTPageLayout>
    );
  }

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="WhatsApp Settings"
      actions={
        <RTButton
          variant="primary"
          onClick={() => navigate('/settings/whatsapp-templates')}
        >
          Template Management
        </RTButton>
      }
      mainSection={true}
    >
      {saving && (
        <div className="whatsapp-settings-loading-overlay">
          <RTSpinner />
        </div>
      )}
      <div className="container-fluid whatsapp-settings-container">
        {settings.length === 0 ? (
          <div className="whatsapp-settings-empty-state">
            <p>
              No WhatsApp settings found. Please configure settings in the
              database.
            </p>
          </div>
        ) : (
          <div className="row">
            {settings
              .filter(
                (setting) =>
                  setting.setting_key !== 'pg_bot_enabled' &&
                  setting.setting_key !== 'pg_bot_enabled_flag'
              )
              .map((setting) => (
              <div key={setting.id} className="col-md-6 mb-4">
                <div className="card whatsapp-settings-card">
                  <div className="card-body whatsapp-settings-card-body">
                    <h5 className="whatsapp-settings-card-title">
                      {setting.setting_key
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </h5>
                    {setting.description && (
                      <p className="whatsapp-settings-description">
                        {setting.description}
                      </p>
                    )}

                    <div className="whatsapp-settings-input-wrapper">
                      {renderSettingInput(setting)}
                    </div>

                    <div className="whatsapp-settings-save-section">
                      <RTButton
                        onClick={() => handleSaveSetting(setting.setting_key)}
                        disabled={
                          saving ||
                          formData[setting.setting_key] ===
                            setting.setting_value
                        }
                        variant="primary"
                        size="small"
                      >
                        Save
                      </RTButton>

                      <div className="whatsapp-settings-last-updated">
                        Last updated:{' '}
                        {setting.updated_at
                          ? new Date(setting.updated_at).toLocaleString()
                          : 'Never'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </RTPageLayout>
  );
};

export default WhatsAppSettings;
